// ─────────────────────────────────────────────
//  boardEditGate — "may this surface write to the board on screen, and if not,
//  what do we TELL the person".
//
//  Extracted from TimelineMdCallRail (2026-08-17 → 2026-08-20), which is still
//  its reference implementation and its most demanding caller. It moved here
//  the moment a SECOND surface needed the same answer: the Doctors Needed box's
//  "Extra doctor" release menu writes to exactly the same roster, so a second
//  copy of this reasoning would have been a second thing to keep in sync with
//  a product decision that has already been revised twice.
//
//  ── WHY THIS IS A DOMAIN MODULE AND NOT A HOOK ───────────────────────────
//  Every input is already resolved by the caller (`useDeviceFacility` does the
//  geolocation work). What is left is a pure decision, which means it can be
//  tested at the truth-table level instead of through a rendered component.
//
//  ⚠️ `campusName` IS INJECTED, NOT IMPORTED. It lives in MdCallEditControls,
//  a component; importing it here would point the domain layer at the view and
//  create exactly the cycle `doctorCoverage` refused when it took its time
//  parser as an argument. A caller with no names to offer gets a gate that
//  still works and a mismatch message that degrades to the generic form.
//
//  ── THE RULES, AND WHOSE DECISIONS THEY ARE ──────────────────────────────
//  1. The caller says this account may already change the board (`canEditBoard`
//     — no new permission is invented here).
//  2. The device has RESOLVED a campus. `checking`, "none" and "ambiguous" are
//     all NO: "we could not tell" is never permission.
//  3. The row/board being written IS the campus on screen, because every write
//     lands on the VIEWED board.
//
//  ⚠️ AN ADMIN HAS NO GEOFENCE (product owner, 2026-08-17 and 2026-08-20).
//  Admins support every campus and are routinely nowhere near the one they are
//  repairing, so rule 2 would lock out the only people who can fix a board
//  remotely. `isAdmin` skips the DEVICE conditions entirely — it never skips
//  the correctness rule that the write targets the board on screen.
// ─────────────────────────────────────────────

// The generic form of the mismatch message, used when the caller injected no
// name resolver. It says the same true thing with less detail — never nothing.
const UNNAMED_CAMPUS = "another campus";

/**
 * @param {object}   input
 * @param {boolean}  input.canEditBoard      Account-level permission, already decided.
 * @param {object}   input.deviceFacility    The whole return of `useDeviceFacility`:
 *                                           { facilityId, verification, reason, checking, retry }.
 * @param {string}   input.viewedFacilityId  The campus THIS board is showing.
 * @param {boolean}  input.isAdmin           Skips the device conditions, never the correctness one.
 * @param {function} input.campusName        Optional (id) => string, injected by the view.
 * @returns {{ gateOpen: boolean, editableCampusId: string|null,
 *             gateNote: string|null, canRetry: boolean, deviceCampusId: string|null }}
 */
export function boardEditGate({
  canEditBoard = false,
  deviceFacility = null,
  viewedFacilityId = null,
  isAdmin = false,
  campusName = null,
} = {}) {
  const nameOf = (id) => {
    if (typeof campusName !== "function") return UNNAMED_CAMPUS;
    const name = campusName(id);
    // A resolver that returns nothing useful must not produce "This board is
    // undefined" — fall back to the same words the uninjected path uses.
    return name || UNNAMED_CAMPUS;
  };

  // ⚠️ "VERIFIED", NOT MERELY "NON-NULL". `useDeviceFacility` also returns a
  // facilityId with verification "device" — a value somebody once tapped into
  // the picker and localStorage kept. That survives `ambiguous`, `denied` and
  // every other fail-closed outcome by design (a wall must still render a
  // board), so treating it as permission would grant edit rights on a device
  // that cannot tell where it is. Only a geofence resolution opens this gate.
  const deviceCampusId =
    deviceFacility && !deviceFacility.checking && deviceFacility.verification === "verified"
      ? deviceFacility.facilityId || null
      : null;

  // A missing `viewedFacilityId` closes the gate rather than opening it: a
  // caller that forgot the prop must lose edit mode, not gain an unchecked one.
  const boardIsDeviceCampus = !!deviceCampusId && viewedFacilityId === deviceCampusId;

  // An admin needs no geofence, but still needs a board to write to: every write
  // targets the VIEWED board, so without one there is no coherent target.
  const gateOpen = !!canEditBoard && (isAdmin ? !!viewedFacilityId : boardIsDeviceCampus);

  // ⚠️ THE CAMPUS A WRITE MUST TARGET IS THE BOARD ON SCREEN, NOT THE DEVICE.
  // This is a CORRECTNESS rule and it survives the admin bypass: departing a
  // Tampa doctor while looking at Wesley Chapel would write Tampa's change into
  // Wesley's board.
  const editableCampusId = gateOpen ? viewedFacilityId : null;

  // One short line, naming the actual obstacle. Never a silent absence of the
  // control: on a surface that normally carries none, "no Edit here" and "Edit
  // is switched off because of X" look identical, and only the second is
  // something a reader can act on.
  let gateNote = null;
  let canRetry = false;
  if (canEditBoard && !gateOpen) {
    if (isAdmin) {
      // The only way an admin is refused: there is no board on screen to write
      // to. Saying "this device cannot confirm its campus" here would be a lie —
      // an admin was never asked to be anywhere.
      gateNote = "No campus is selected on this board, so there is nothing to edit.";
    } else if (!deviceFacility) {
      // No hook wired at all. Not "checking" — nothing is looking.
      gateNote = "This device cannot confirm which campus it is at, so editing is off.";
    } else if (deviceFacility.checking) {
      gateNote = "Checking which campus this device is at — editing stays off until it is confirmed.";
    } else if (!deviceCampusId) {
      canRetry = typeof deviceFacility.retry === "function";
      gateNote =
        deviceFacility.reason === "ambiguous"
          ? "This device is in range of more than one campus, so editing is off."
          : "This device cannot confirm which campus it is at, so editing is off.";
    } else {
      // Resolved, but this board is another campus. Retry cannot help — moving
      // the board back to this campus can, so say which two they are.
      gateNote = `This board is ${nameOf(viewedFacilityId)} and this device is at ${nameOf(deviceCampusId)}, so editing is off.`;
    }
  }

  return { gateOpen, editableCampusId, gateNote, canRetry, deviceCampusId };
}

export default boardEditGate;
